// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, Input, ChangeDetectionStrategy, ViewChild, SimpleChanges, ChangeDetectorRef, NgZone } from "@angular/core";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion'
import { MatDividerModule } from '@angular/material/divider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { defer, forkJoin, of, BehaviorSubject, firstValueFrom } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Analysis } from "../../../interfaces/analysis";
import { HceAnnotation } from '../../../interfaces/hce/hce-annotation';
import { SvResponse, Sv } from '../../../interfaces/hce/hce-sv';
import { OverlapIndex, BaseItem } from '../../../interfaces/hce/hce-results-summary-view';
import { SvChrOpts, SvPieChartData } from '../../../interfaces/hce/hce-stats';
import { HceSvPieChartComponent } from '../../../shared/hce/sv-pie-chart/sv-pie-chart.component';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { HceService } from '../../../services/hce/hce.service';
import { HceResultsSummaryViewService } from '../../../services/hce/hce-results-summary-view/hce-results-summary-view.service';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { ChromosomeViewComponent } from "./chromosome-view/chromosome-view.component";

@Component({
  selector: 'app-hce-results-summary-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatRadioModule,
    MatExpansionModule,
    MatDividerModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    HceSvPieChartComponent,
    ChromosomeViewComponent,
    ButtonsComponent
  ],
  templateUrl: './hce-results-summary-view.component.html',
  styleUrl: './hce-results-summary-view.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HceResultsSummaryViewComponent {
  @Input() analysis!: Analysis | null;
  @Input() sv!: SvResponse;
  @Input() refNameMap!: Record<string, string>;
  @Input() filterRefNameMap!: Record<string, string>;
  @Input() selectedAnnotationFiles: string[] = [];
  @Input() selectedAnnotationByFile: Record<string, HceAnnotation[]> = {};
  @ViewChild('chrview') private chrview?: ChromosomeViewComponent;

  // Pie Chart
  chrOpts?: SvChrOpts = {};
  pieChartData?: SvPieChartData = {};

  // UI State
  _isReady = false;
  selectedSample: string | null = null;
  hideSharedSv = false;
  overlapPct = 1;
  filterExpanded = false;
  sampleCounts: Record<string, number> = {};
  hasError = false;

  btnNameChr: string = $localize`Chr`;
  btnNameSv: string = $localize`Sv`;
  btnNameClear: string = $localize`Clear`;
  btnMatFlat: string = 'mat-flat-button';
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';

  private _lastVm: {
    svs: Sv[];
    mapping: Record<string, string>
  } | null = null;
  private _index: OverlapIndex | null = null;

  private _busyCount = 0;
  private _busy$ = new BehaviorSubject<boolean>(false);
  get isBusy(): boolean { return this._busy$.value; }
  get svs(): Sv[] { return Object.values(this.sv.svs ?? {}) as Sv[] }

  constructor(
    private hceService: HceService,
    private rsvSercice: HceResultsSummaryViewService,
    private messageModal: MessageModalService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) { }

  // Initial load error (results summary)
  private showLoadFailed(): void {
    this.messageModal.openModal(
      $localize`Error`,
      $localize`Failed to load results summary.`
    );
  }

  vm$ = defer(() => {
    // If the analysis is invalid, terminate immediately (display modal)
    if (!this.analysis?.AnalysisID || !this.analysis?.PanelID) {
      this.hasError = true;
      this.showLoadFailed();
      return of(null);
    }

    const a = this.analysis;
    return forkJoin({
      refs: this.hceService.getView(a.PanelID),
      contigsTxt: this.hceService.getCoverage(a.AnalysisID),
      db: this.hceService.getUcscRefVersion(a.PanelID),
      gender: this.hceService.getGender(a.AnalysisID),
    }).pipe(
      map(({ refs, contigsTxt, db, gender }) => {
        // Result Summary requires all of the following data.
        if (
          !refs || refs.length === 0 ||
          !contigsTxt ||
          !db
        ) {
          this.hasError = true;
          this.showLoadFailed();
          return null;
        }

        return {
          references: refs,
          contigs: this.rsvSercice.parseContigs(contigsTxt),
          db: db,
          gender: this.rsvSercice.getGenderLabel(gender),
        };
      }),
      tap((vm) => {
        if (vm) {
          this.ngZone.runOutsideAngular(() => {
            this.buildSharedIndex();
            this.recomputeCounts();
          });
        } else {
          this.sampleCounts = {};
          this.cdr.markForCheck();
        }
      }),
      catchError(err => {
        // Network/server exceptions and the like are collectively handled here in a modal.
        console.error(err);
        this.hasError = true;
        this.showLoadFailed();
        return of(null);
      }),
      shareReplay(1)
    );
  });

  trackByFile = (_: number, f: string) => f;
  onChrViewReady() {
    this._isReady = true;
    if (this.selectedAnnotationFiles.length !== 0) this.applySvVisibilitySnapshot();
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['sv'] || changes['filterRefNameMap']) {
      this._lastVm = { svs: this.svs, mapping: this.filterRefNameMap };
      const stats = this.rsvSercice.getStatsSampleSVData(this.sv as any, this.filterRefNameMap);
      if (stats.success) {
        this.chrOpts = stats.chrOpts;
        this.pieChartData = stats.pieChartData;
      } else {
        this.chrOpts = { 0: "ALL" };
        this.pieChartData = { 0: { "values": [], "total": 0, "seriesColors": [] } };
      }
      if (!this.chrview) return;
      this.rebuild();
    }
  }

  onSampleChange() {
    if (this.selectedAnnotationFiles.length == 0) return;
    this.applySvVisibilitySnapshot();
  }

  onClearClick() {
    this.hideSharedSv = false;
    this.selectedSample = null;
    this.chrview?.onClearClick();
    this.cdr.markForCheck();
  }

  onOverlapCommit() {
    if (this.selectedAnnotationFiles.length == 0) return;
    this.applySvVisibilitySnapshot();
    if (this._index) {
      this.ngZone.runOutsideAngular(() => this.recomputeCounts());
    } else {
      this.sampleCounts = {};
    }
  }

  onOverlapPreview(ev: Event) {
    const valueStr = (ev?.target as HTMLInputElement | null)?.value ?? `${this.overlapPct}`;
    const v = Number(valueStr);
    const n = Math.max(0, Math.min(100, Math.round(Number.isFinite(v) ? v : this.overlapPct)));
    if (n !== this.overlapPct) { this.overlapPct = n; this.cdr.markForCheck(); }
  }

  toggleHideSharedSv(next: boolean) {
    this.hideSharedSv = next;
    if (this.selectedAnnotationFiles.length == 0) return;
    this.applySvVisibilitySnapshot();
  }

  // Execute rebuild + recalculation together with busy display
  private rebuild(): void {
    this.withBusy(async () => {
      this._isReady = false;
      this.cdr.markForCheck();

      await new Promise<void>((resolve) => {
        this.ngZone.runOutsideAngular(() => {
          try {
            this.chrview!.rebuild();
          } finally {
            resolve();
          }
        });
      });

      await new Promise<void>((resolve) => {
        this.ngZone.runOutsideAngular(() => {
          try {
            this.buildSharedIndex();
            this.recomputeCounts();
          } finally {
            resolve();
          }
        });
      });

      this.ngZone.run(() => {
        this.cdr.markForCheck();
      });
    });
  }

  /** Build the precomputed index only once when the data (sv/annotation) is ready */
  private buildSharedIndex(): void {
    const vm = this._lastVm;
    if (!vm) { this._index = null; return; }

    const id2chr = vm.mapping ?? {};
    const sampleName = this.analysis?.SampleName ?? 'SAMPLE';
    const groups = new Map<string, BaseItem[]>();

    // Assign runtime index to SV (required for visibility/HL application)
    const svs = (vm.svs ?? []).map((sv, i) => ({ ...sv, __index__: i }));
    this._lastVm = { ...vm, svs };

    // sample group
    groups.set(sampleName,
      svs.map(sv => this.rsvSercice.toItemFromSv(sv, sampleName, id2chr))
        .filter((x): x is BaseItem => !!x)
    );

    // Annotations
    const validChrs = new Set(Object.values(id2chr));
    for (const file of Object.keys(this.selectedAnnotationByFile ?? {})) {
      const src = (this.selectedAnnotationByFile[file] ?? []).map((a, i) => ({ ...a, __index__: i }));
      const items = src
        .map(a => this.rsvSercice.toItemFromAnno(a, file, validChrs))
        .filter((x): x is BaseItem => !!x);
      if (items.length) groups.set(file, items);
    }

    // Index construction as a service + pre-calculation of maxRatio for parent
    const idx = this.rsvSercice.buildIndex(groups);
    this._index = this.rsvSercice.precomputeMaxRatio(idx);
  }

  /** Recalculate and reflect the unique count for label display in the service */
  private recomputeCounts(): void {
    if (!this._index) { this.sampleCounts = {}; this.cdr.markForCheck(); return; }
    const rate = this.overlapPct / 100;
    this.sampleCounts = this.rsvSercice.countUniques(this._index, rate);
    this.cdr.markForCheck();
  }

  /** Perform visibility/highlight determination on the parent, and apply that 'result snapshot' to the child */
  private applySvVisibilitySnapshot(): void {
    if (!this.chrview || !this._index) return;
    const result = this.rsvSercice.evaluateVisibility(this._index, {
      selectedGroup: this.selectedSample ?? null,
      overlapRate01: this.overlapPct / 100,
      hideShared: this.hideSharedSv,
    });

    // Apply to child all at once
    this.chrview.applyVisibilitySnapshot({
      svVisible: result.svVisible,
      svHighlight: result.svHighlight,
      annoVisible: result.annoVisible,
      annoHighlight: result.annoHighlight,
      selectedSample: this.selectedSample ?? null,
      hideSharedSv: this.hideSharedSv,
    });
  }

  private pushBusy() {
    this._busyCount++;
    if (!this._busy$.value) { this._busy$.next(true); }
  }

  private popBusy() {
    this._busyCount = Math.max(0, this._busyCount - 1);
    if (this._busyCount === 0 && this._busy$.value) { this._busy$.next(false); }
  }

  /** Give the UI a chance to redraw (next tick or rAF) */
  private nextFrame(): Promise<void> {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => setTimeout(() => resolve(), 0));
    });
  }

  /** Help that makes heavy processing 'start with a busy display + on the next frame' */
  private async withBusy<T>(work: () => Promise<T> | T): Promise<T> {
    this.pushBusy();
    // Reflect the flag ON
    this.cdr.markForCheck();
    await this.nextFrame();
    try {
      const ret = await work();
      return ret;
    } finally {
      this.popBusy();
      this.cdr.markForCheck();
    }
  }
}